import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { MembersRole, PermissionsService } from '@tgx/shared/enums';
import { MemberInfo } from '@tgx/shared/interfaces';
import { PermissionsUpdate } from '../../interfaces/permissions-configuration.interface';
import { UserPermissionsService } from '../../services/user-permissions.service';

type SharedPermissionsUpdate = Partial<Record<PermissionsService, MembersRole | null>>;

@Component({
  selector: 'tgx-user-permissions',
  templateUrl: './user-permissions.component.html',
  styleUrls: ['./user-permissions.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserPermissionsComponent implements OnChanges {
  private settingsPermissionsService = inject(UserPermissionsService);

  @Input() user: MemberInfo | null = null;
  @Input() orgCode: string;
  @Input() canEditRoles: boolean;

  @Output() permissionschanged = new EventEmitter<PermissionsUpdate>();
  @Output() allUserPermissions = new EventEmitter<PermissionsUpdate>();
  @Output() previousStep = new EventEmitter<number>();
  @Output() invalidForm = new EventEmitter<boolean>();
  @Output() billingSelectionInvalid = new EventEmitter<boolean>();

  public isLoading = true;
  public initialPermissions: SharedPermissionsUpdate = {};
  public mainRole: MembersRole | null = null;

  private readonly services = [
    PermissionsService.HOTELX,
    PermissionsService.ENTITIES,
    PermissionsService.BILLING,
    PermissionsService.USER,
    PermissionsService.FASTX,
  ];

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['user'] || changes['orgCode']) {
      this.mainRole = this.user?.role ?? null;

      if (this.user?.email) {
        this.isLoading = true;
        await this.getUserPermissions();
      } else {
        this.isLoading = false;
        this.initialPermissions = {};
        this.invalidForm.emit(true);
      }
    }
  }

  private async getUserPermissions(): Promise<void> {
    try {
      const userEmail = this.user?.email;
      if (!userEmail) return;

      const res = await this.settingsPermissionsService.getMemberRoleServiceInfo(userEmail);
      const org = res.organizations.find((o) => o.organization.code === this.orgCode);

      if (org) {
        const initialPermissions: PermissionsUpdate = {};

        this.services.forEach((service) => {
          const found = org.services.find((s) => s.service === service);
          const currentRole = found?.role;

          if (currentRole) {
            initialPermissions[service] = currentRole;
          }
        });

        this.allUserPermissions.emit(initialPermissions);
        this.initialPermissions = initialPermissions;
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    } finally {
      this.isLoading = false;
    }
  }

  protected handlePermissionsChanged(changes: SharedPermissionsUpdate): void {
    this.permissionschanged.emit(changes as PermissionsUpdate);
  }

  protected handleInvalidForm(isInvalid: boolean): void {
    this.invalidForm.emit(isInvalid);
  }

  protected handleBillingSelectionInvalid(isInvalid: boolean): void {
    this.billingSelectionInvalid.emit(isInvalid);
  }

  protected handlePreviousStep(step: number): void {
    this.previousStep.emit(step);
  }

  protected get canEditTargetRoles(): boolean {
    return this.canEditRoles && this.mainRole !== MembersRole.OWNER;
  }
}
